import { getTradeInLabel, getTradeInNegativeEquity } from '../ducks/tradeIn.duck';
import { formatCAD } from '../containers/PaymentCalculator/calculationEngine';

/**
 * Summary lines for the three My Transaction cards.
 *
 * These live in one place because the rules they encode are easy to get
 * subtly wrong and worse to get wrong in only one of two copies:
 *
 *  - a garage that failed to load is not an empty garage
 *  - a financing status that could not be checked is not "no application"
 *  - an unrecognised financing value is not an approval
 *
 * Desktop and mobile both render from here so those distinctions cannot
 * drift apart.
 */

export const FINANCE_STATUS_LABELS = {
  approved: 'Approved',
  conditionally_approved: 'Conditionally approved',
  in_review: 'In review',
  declined: 'Not approved',
};

/**
 * @param {Object} garage  the myGarage slice
 * @param {boolean} isSignedIn
 * @returns {string|null} null while there is nothing meaningful to say yet
 */
export const garageSummary = (garage, isSignedIn) => {
  if (!isSignedIn) return 'Sign in to see your vehicles';
  if (garage.fetchInProgress) return 'Loading…';
  // A failed load must not read as an empty garage.
  if (garage.fetchError) return "Couldn't load right now";
  if (!garage.loaded) return null;
  if (garage.totalItems === 0) return 'No vehicles yet';

  const [first] = garage.listings;
  const firstTitle = first?.title || 'Untitled listing';
  const remaining = garage.totalItems - 1;
  return remaining > 0 ? `${firstTitle} +${remaining} more` : firstTitle;
};

/**
 * @param {Object|null} tradeIn  the saved record, or null
 * @param {boolean} isSignedIn
 */
export const tradeInSummary = (tradeIn, isSignedIn) => {
  if (!isSignedIn) return 'Sign in to add your trade-in';
  if (!tradeIn) return 'Add your trade-in';

  const label = getTradeInLabel(tradeIn) || 'Saved';
  const negativeEquity = getTradeInNegativeEquity(tradeIn);
  // getTradeInNegativeEquity returns null when either figure is missing, so
  // this never claims a position that was not actually calculated.
  return negativeEquity > 0 ? `${label} · ${formatCAD(negativeEquity)} negative equity` : label;
};

/**
 * @param {Object} finance  the financeStatus slice
 * @param {boolean} isSignedIn
 */
export const financeSummary = (finance, isSignedIn) => {
  if (!isSignedIn) return 'Sign in to see your status';
  if (finance.fetchInProgress) return 'Loading…';
  // "Could not check" must never be reported as "no application on file" —
  // that would state something we did not check, to someone who may well
  // have a live application.
  if (finance.fetchError || (finance.loaded && !finance.available)) {
    return 'Status unavailable';
  }
  if (!finance.loaded) return null;
  // Any status not on the allowlist falls through to "no application"
  // rather than being shown as something approval-shaped.
  return FINANCE_STATUS_LABELS[finance.status] || 'No application on file';
};
